import { useCallback, useEffect, useState } from 'react';

/* The three knobs the design canvas exposed as props, handed to the reader.
   Their own storage key, so clearing your writing keeps your typography. */

const PREFS_KEY = 'longhand-prefs-v1';

export const KNOBS = [
  { key: 'editorWidth',    label: 'Page width',  cssVar: '--editor-width',       min: 560, max: 920, step: 10,  unit: 'px' },
  { key: 'editorFontSize', label: 'Text size',   cssVar: '--editor-font-size',   min: 16,  max: 24,  step: 1,   unit: 'px' },
  { key: 'lineHeight',     label: 'Line height', cssVar: '--editor-line-height', min: 1.5, max: 2.1, step: 0.05, unit: '' }
];

/* Not a knob: the inspector's width is dragged, not slid, and it is not
   typography — the Aa bar's Reset leaves it alone. The design's 252 was sized
   for two readouts; the same column now has to hold a conversation, so it
   starts a little wider and the writer moves it from there. */
export const INSPECTOR = { min: 240, max: 720, step: 16 };

/* The document list on the other side, dragged the same way. Long titles are
   the reason to widen it; past a third of a laptop screen it is taking the
   page's room for a list. */
export const SIDEBAR = { min: 200, max: 480, step: 16 };

/* Three states, not two. "System" is the one most people want — dark when the
   desk is dark — and it has to be a real choice rather than the absence of one,
   because someone who prefers light all day should not have the app change
   under them at sunset. */
export const THEMES = ['system', 'light', 'dark'];
export const THEME_LABEL = { system: 'Auto', light: 'Light', dark: 'Dark' };
const DARK_QUERY = '(prefers-color-scheme: dark)';

export const PREF_DEFAULTS = {
  editorWidth: 680, editorFontSize: 19, lineHeight: 1.8,
  typeBar: false, inspectorWidth: 320, sidebarWidth: 272, theme: 'system'
};

/** Whether the OS is asking for dark. False anywhere matchMedia is missing. */
export const systemPrefersDark = () => !!globalThis.matchMedia?.(DARK_QUERY).matches;

/** What `theme` actually resolves to right now. */
export const resolveTheme = theme =>
  (theme === 'light' || theme === 'dark' ? theme : (systemPrefersDark() ? 'dark' : 'light'));

/**
 * Stamp the resolved theme on <html>. Everything visual keys off this one
 * attribute, so there is a single place where "it is dark now" becomes true.
 * The same decision is made again, in four lines, by the script in index.html —
 * that copy exists so the first paint is already the right colour.
 */
export function applyTheme(theme) {
  const resolved = resolveTheme(theme);
  document.documentElement.dataset.theme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#14181c' : '#f2f2ef');
  return resolved;
}

export const clampInspector = w =>
  Math.round(Math.min(INSPECTOR.max, Math.max(INSPECTOR.min, Number(w) || PREF_DEFAULTS.inspectorWidth)));

export const clampSidebar = w =>
  Math.round(Math.min(SIDEBAR.max, Math.max(SIDEBAR.min, Number(w) || PREF_DEFAULTS.sidebarWidth)));

function read() {
  const p = { ...PREF_DEFAULTS };
  try {
    const saved = JSON.parse(localStorage.getItem(PREFS_KEY) || 'null');
    if (!saved) return p;
    for (const k of KNOBS) {
      const v = Number(saved[k.key]);
      if (Number.isFinite(v)) p[k.key] = Math.min(k.max, Math.max(k.min, v));
    }
    p.typeBar = !!saved.typeBar;
    if (saved.inspectorWidth !== undefined) p.inspectorWidth = clampInspector(saved.inspectorWidth);
    if (saved.sidebarWidth !== undefined) p.sidebarWidth = clampSidebar(saved.sidebarWidth);
    if (THEMES.includes(saved.theme)) p.theme = saved.theme;
  } catch (e) {}
  return p;
}

export const knobText = (k, prefs) => (k.step < 1 ? String(Math.round(prefs[k.key] * 100) / 100) : prefs[k.key] + k.unit);
export const atDefaults = prefs => KNOBS.every(k => prefs[k.key] === PREF_DEFAULTS[k.key]);

export function usePrefs() {
  const [prefs, setPrefs] = useState(read);

  useEffect(() => {
    const root = document.documentElement;
    for (const k of KNOBS) root.style.setProperty(k.cssVar, prefs[k.key] + k.unit);
    applyTheme(prefs.theme);
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch (e) {}
  }, [prefs]);

  // Following the system means following it while the app is open: at sunset
  // macOS flips and the page should flip with it, with nobody reloading.
  useEffect(() => {
    if (prefs.theme !== 'system') return;
    const mq = globalThis.matchMedia?.(DARK_QUERY);
    if (!mq?.addEventListener) return;
    const onShift = () => applyTheme('system');
    mq.addEventListener('change', onShift);
    return () => mq.removeEventListener('change', onShift);
  }, [prefs.theme]);

  const set = useCallback((patch) => setPrefs(p => ({ ...p, ...patch })), []);
  // Reset is the Aa bar's, so it resets what the Aa bar changes and nothing
  // else — not the open/closed bars, not the widths someone dragged.
  const reset = useCallback(() => setPrefs(p => {
    const next = { ...p };
    for (const k of KNOBS) next[k.key] = PREF_DEFAULTS[k.key];
    return next;
  }), []);
  return [prefs, set, reset];
}
